
import { Vue, Component, Watch } from 'vue-property-decorator'
import FivePercentRule from '@/types/fivePercentRule'
import Column from '@/types/column'
import TableSettings from '@/types/tableSettings'
import ActiveFilters from '@/components/listview/ActiveFilters.vue'
import Pagination from '@/types/pagination'
import axios from 'axios'
import PageHeader from '@/components/base/PageHeader.vue'
import SearchableColumnFilter from '@/components/base/SearchableColumnFilter.vue'
import ls from '@/ls'
import { can } from '@/globalFunctions'

@Component({
  methods: { can },
  components: { SearchableColumnFilter, PageHeader, ActiveFilters }
})

export default class FivePercentRules extends Vue {
  settings = {
    permissionNames: { show: 'buildings.fivePercent.view', edit: 'buildings.fivePercent.edit' },
    functionNames: { getList: 'getFivePercentRuleList' },
    getterNames: { list: 'fivePercentRuleList', loading: 'loadingFivePercentRule' },
    localStorageName: 'fivePercentRuleTableSettings'
  }

  columns:Column[] = [
    {
      title: 'Entiteit',
      key: 'entity.id',
      dataIndex: 'entity',
      scopedSlots: { customRender: 'entity' },
      filters: []
    },
    {
      title: 'Project',
      key: 'project.id',
      dataIndex: 'project',
      scopedSlots: { customRender: 'project', filterDropdown: 'customFilter' },
      filters: []
    },
    {
      title: 'Bouwnummer',
      key: 'building.number',
      dataIndex: 'building',
      scopedSlots: { customRender: 'building' },
      sorter: true
    },
    {
      title: 'Vastgehouden',
      key: 'hold',
      scopedSlots: { customRender: 'hold' },
      filters: [{ text: 'Ja', value: 'yes' }, { text: 'Nee', value: 'no' }]
    },
    {
      title: 'Bedrag',
      key: 'hold_amount',
      scopedSlots: { customRender: 'hold_amount' },
      sorter: true
    },
    {
      title: 'Alles opgelost',
      key: 'all_resolved_sent_at',
      scopedSlots: { customRender: 'all_resolved' },
      sorter: true,
      filters: [{ text: 'Ja', value: 'yes' }, { text: 'Nee', value: 'no' }]
    },
    {
      title: 'Verzoek tot vrijgave',
      key: 'request_for_release_sent_at',
      scopedSlots: { customRender: 'request_for_release' },
      sorter: true,
      filters: [{ text: 'Verstuurd', value: 'yes' }, { text: 'Niet verstuurd', value: 'no' }]
    },
    {
      title: 'Herinnering',
      key: 'request_for_release_reminder_sent_at',
      scopedSlots: { customRender: 'request_for_release_reminder' },
      sorter: true,
      filters: [{ text: 'Verstuurd', value: 'yes' }, { text: 'Niet verstuurd', value: 'no' }]
    },
    {
      title: 'Regeling',
      key: 'rule_type',
      scopedSlots: { customRender: 'rule_type' },
      sorter: false,
      filters: [{ text: 'Huidige regeling', value: 'new' }, { text: 'Oude regeling', value: 'old' }, { text: 'Onbekend', value: 'unknown' }]
    },
    {
      title: 'Behandelaar',
      key: 'manager_id',
      dataIndex: 'manager',
      scopedSlots: { customRender: 'manager' },
      filters: []
    },
    {
      title: 'Actie\'s',
      key: 'action',
      scopedSlots: { customRender: 'action' }
    }
  ]

  tableSettings:TableSettings = ls.get(this.settings.localStorageName) || {
    pagination: { current: 1, pageSize: 25 },
    showSizeChanger: true,
    pageSizeOptions: ['25', '50', '100'],
    activeFilters: {},
    sort: null,
    search: null
  }

  downloading = false

  mounted () : void {
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
    this.getFilters()
    this.onTableSettingsChange()
    this.HookCustomColumFilterEvents()
  }

  filterVisible = false
  HookCustomColumFilterEvents () : void {
    this.columns.forEach((column:Column) => {
      if (column.scopedSlots?.filterDropdown !== undefined) {
        column.onFilterDropdownVisibleChange = (visible: boolean) => {
          this.filterVisible = visible
        }
      }
    })
  }

  getFilters () : void {
    this.getProjectFilter()
    this.getEntityFilter()
    this.getManagerFilter()
  }

  getEntityFilter () : void {
    axios.post(process.env.VUE_APP_API_URL + '/filters/entity/five_percent_rule', { textColumn: 'name', valueColumn: 'id' }).then((res) => {
      this.columns[0].filters = res.data
    })
  }

  getProjectFilter () : void {
    axios.post(process.env.VUE_APP_API_URL + '/filters/project/five_percent_rule', { textColumn: 'name', valueColumn: 'id' }).then((res) => {
      this.columns[1].filters = res.data
    })
  }

  getManagerFilter () : void {
    axios.post(process.env.VUE_APP_API_URL + '/filters/user/five_percent_rules_to_manage', { textColumn: 'full_name', valueColumn: 'id' }).then((res) => {
      this.columns[9].filters = res.data
    })
  }

  exportOverview () : void {
    this.downloading = true
    axios.post(process.env.VUE_APP_API_URL + '/fivePercentRules/export/overview', {}, { responseType: 'arraybuffer' }).then((r) => {
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = 'Huidige_stand_5p_bankgarantie_' + this.currentDateTime() + '.xlsx'
      link.click()
    }).finally(() => {
      this.downloading = false
    })
  }

  currentDateTime () : string {
    const now = new Date()
    const day = String(now.getDate()).padStart(2, '0')
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const year = now.getFullYear()
    const hours = String(now.getHours()).padStart(2, '0')
    const minutes = String(now.getMinutes()).padStart(2, '0')

    return `${day}-${month}-${year}_${hours}${minutes}`
  }

  handleTableChange (pagination:{ current:number, pageSize:number }, filters:{[key: string]: number[]|string[]}, sort:{ columnKey:string, order:string }) : void {
    // Remove key from filters if filter is an empty object.
    if (filters && Object.keys(filters).length) {
      Object.keys(filters).forEach((key) => {
        if (!filters[key] || !filters[key].length) {
          delete filters[key]

          this.columns.forEach((column:Column) => {
            if (column.key === key) {
              column.filteredValue = null
            }
          })
        }
      })
    }

    // Set pagination, filters and sorting on tableSettings.
    this.tableSettings.pagination = pagination
    this.tableSettings.activeFilters = filters
    this.tableSettings.sort = sort.order ? sort : null

    // Get list based on filters and search term.
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  handleSearchChange () : void {
    // Set page to 1 because of new search but retain filters and sorting.
    this.tableSettings.pagination.current = 1

    // Get list based on filters and search term.
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  // Required for ActiveFilters component
  updateFilters (filters: {[key: string]: number[]|string[]|boolean[]}) : void {
    const { columns } = this

    columns.forEach((column:Column) => {
      column.filteredValue = column.key in filters ? filters[column.key] : []
    })

    this.tableSettings.activeFilters = Object.assign({}, filters)
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  reloadList () : void {
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  get itemList () : { data: FivePercentRule[], meta: { per_page:string, total:number } } {
    return this.$store.getters[this.settings.getterNames.list]
  }

  get pagination () : Pagination {
    return {
      current: this.tableSettings.pagination.current,
      defaultPageSize: this.tableSettings.pagination.pageSize,
      pageSize: parseInt(this.itemList.meta.per_page),
      total: this.itemList.meta.total,
      showTotal: (total, range) => `5% regeling ${range[0]} t/m ${range[1]} van de ${total}`,
      showSizeChanger: this.tableSettings.showSizeChanger,
      pageSizeOptions: this.tableSettings.pageSizeOptions
    }
  }

  get loading () : boolean {
    return this.$store.getters[this.settings.getterNames.loading].list
  }

  @Watch('tableSettings', { deep: true })
  onTableSettingsChange () : void {
    // On tableSettings change, store them in local storage.
    ls.set(this.settings.localStorageName, this.tableSettings)

    axios.post(process.env.VUE_APP_API_URL + '/users/admin/settings', {
      settingName: this.settings.localStorageName,
      settingValue: this.tableSettings
    })

    // Set the current active filters on the columns.
    this.columns.forEach((column:Column) => {
      // Set active filters.
      if (this.tableSettings.activeFilters && column.key in this.tableSettings.activeFilters) {
        column.filteredValue = this.tableSettings.activeFilters[column.key]
      }

      // Set active sort order.
      if (this.tableSettings.sort && column.key === this.tableSettings.sort.columnKey) {
        column.defaultSortOrder = this.tableSettings.sort.order
        column.sortOrder = this.tableSettings.sort.order
      } else {
        column.sortOrder = false
      }
    })
  }
}
